import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { apiClient } from 'helpers/api_helper';
import { debounce } from 'lodash';

// Type guard to check if a value is a string or number
const isStringOrNumber = (value: any): value is string | number => {
  return typeof value === 'string' || typeof value === 'number';
};

interface SearchableDropdownProps<T> {
  value?: T | null;
  onChange: (item: T | null) => void;
  onSearch: (searchTerm: string) => void;
  options: T[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  displayKey: keyof T;
  secondaryDisplayKey?: keyof T;
  searchKey: keyof T;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  icon?: string;
  renderOption?: (item: T) => React.ReactNode;
  renderSelected?: (item: T) => string | React.ReactNode;
  minSearchLength?: number;
  emptyMessage?: string;
  clearable?: boolean;
  size?: 'sm' | 'lg';
  maxHeight?: string;
}

export function SearchableDropdown<T extends Record<string, any>>({
  value,
  onChange,
  onSearch,
  options = [],
  loading = false,
  error = null,
  placeholder = 'Search...',
  displayKey,
  secondaryDisplayKey,
  searchKey,
  disabled = false,
  required = false,
  label,
  icon,
  renderOption,
  renderSelected,
  minSearchLength = 2,
  emptyMessage = 'No items found',
  clearable = true,
  size,
  maxHeight = '300px'
}: SearchableDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (term.length >= minSearchLength || term.length === 0) {
        onSearch(term);
      }
    }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearch, minSearchLength]
  );

  // Handle search input change
  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle item selection
  const handleSelect = (item: T) => {
    onChange(item);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm('');
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (searchTerm.length >= minSearchLength || minSearchLength === 0) {
      onSearch(searchTerm);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate display text for selected value
  const getDisplayText = (): string => {
    if (!value) return '';
    
    try {
      if (renderSelected) {
        const rendered = renderSelected(value);
        // Convert ReactNode to string if needed
        if (isStringOrNumber(rendered)) {
          return String(rendered);
        } else {
          // Fallback for complex ReactNode - use primary display key
          return String(value[displayKey] || '');
        }
      }
      
      const primary = String(value[displayKey] || '');
      const secondary = secondaryDisplayKey ? String(value[secondaryDisplayKey] || '') : '';
      
      return secondary ? `${primary} - ${secondary}` : primary;
    } catch (error) {
      console.warn('Error rendering display text:', error);
      return String(value[displayKey] || '');
    }
  };

  // Generate safe placeholder text
  const getPlaceholderText = (): string => {
    if (value) {
      return getDisplayText();
    }
    return placeholder || '';
  };

  // Validate required props (after hooks to avoid hooks rules violation)
  if (!displayKey) {
    console.error('SearchableDropdown: displayKey is required');
  }

  if (!onChange || typeof onChange !== 'function') {
    console.error('SearchableDropdown: onChange function is required');
  }

  if (!onSearch || typeof onSearch !== 'function') {
    console.error('SearchableDropdown: onSearch function is required');
  }

  return (
    <div ref={dropdownRef} className="position-relative">
      {label && (
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      
      <InputGroup size={size}>
        {icon && (
          <InputGroup.Text>
            <i className={icon}></i>
          </InputGroup.Text>
        )}
        
        <Form.Control
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholderText()}
          disabled={disabled}
          className={error ? 'is-invalid' : ''}
          autoComplete="on"
        />
        
        {loading && (
          <InputGroup.Text>
            <Spinner size="sm" />
          </InputGroup.Text>
        )}
        
        {value && clearable && !disabled && (
          <InputGroup.Text 
            role="button" 
            onClick={handleClear}
            className="text-muted"
            style={{ cursor: 'pointer' }}
          >
            <i className="bx bx-x"></i>
          </InputGroup.Text>
        )}
        
        <InputGroup.Text 
          role="button" 
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className="text-muted"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'}`}></i>
        </InputGroup.Text>
      </InputGroup>

      {error && (
        <div className="invalid-feedback d-block">
          {error}
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
          style={{ 
            zIndex: 1050, 
            maxHeight, 
            overflowY: 'auto',
            borderColor: '#dee2e6'
          }}
        >
          {loading && (
            <div className="p-3 text-center">
              <Spinner size="sm" className="me-2" />
              <span className="text-muted">Searching...</span>
            </div>
          )}

          {error && !loading && (
            <div className="p-3 text-center text-danger">
              <i className="bx bx-error-circle me-2"></i>
              {error}
            </div>
          )}

          {!loading && !error && options.length === 0 && (searchTerm.length >= minSearchLength || minSearchLength === 0) && (
            <div className="p-3 text-center text-muted">
              <i className="bx bx-search-alt me-2"></i>
              {emptyMessage}
            </div>
          )}

          {!loading && !error && searchTerm.length > 0 && searchTerm.length < minSearchLength && minSearchLength > 0 && (
            <div className="p-3 text-center text-muted">
              <i className="bx bx-info-circle me-2"></i>
              Type at least {minSearchLength} characters to search
            </div>
          )}

          {!loading && !error && options.length > 0 && (searchTerm.length >= minSearchLength || minSearchLength === 0) && (
            <>
              {options.map((option, index) => {
                // Generate unique key safely
                const key = option.id || option[displayKey] || index;
                
                return (
                  <div
                    key={String(key)}
                    className={`dropdown-item cursor-pointer ${
                      index === highlightedIndex ? 'active' : ''
                    }`}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: index === highlightedIndex ? '#0d6efd' : undefined,
                      color: index === highlightedIndex ? 'white' : undefined
                    }}
                  >
                    {renderOption ? (
                      <div>
                        {renderOption(option)}
                      </div>
                    ) : (
                      <div>
                        <div className="fw-medium">
                          {String(option[displayKey] || '')}
                        </div>
                        {secondaryDisplayKey && (
                          <small className={`text-${index === highlightedIndex ? 'light' : 'muted'}`}>
                            {String(option[secondaryDisplayKey] || '')}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface ProductSearchDropdownProps {
  value?: any;
  onChange: (product: any) => void;
  onSearch: (searchTerm: string, type: 'bagNo' | 'styleNo' | 'igiNo' | 'huid') => void;
  options: any[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  label?: string;
  required?: boolean;
  searchType: 'bagNo' | 'styleNo' | 'igiNo' | 'huid';
}

export const ProductSearchDropdown: React.FC<ProductSearchDropdownProps> = ({
  value,
  onChange,
  onSearch,
  options,
  loading,
  error,
  placeholder,
  label,
  required,
  searchType
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      if (term.length >= 2) {
        onSearch(term, searchType);
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    },
    [onSearch, searchType]
  );

  const handleSelect = useCallback(
    (product: any) => {
      onChange(product);
      setSearchTerm('');
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <Form.Group className="mb-3">
      {label && <Form.Label>{label}{required && <span className="text-danger">*</span>}</Form.Label>}
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => options.length > 0 && setIsOpen(true)}
          className={error ? 'is-invalid' : ''}
        />
        {loading && (
          <div className="position-absolute end-0 top-50 translate-middle-y me-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {error && <div className="invalid-feedback">{error}</div>}
        {isOpen && options.length > 0 && (
          <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000 }}>
            {options.map((option, index) => (
              <div
                key={index}
                className="p-2 cursor-pointer hover-bg-light"
                onClick={() => handleSelect(option)}
                style={{ cursor: 'pointer' }}
              >
                <div className="fw-bold">{option.displayText}</div>
                <div className="small text-muted">{option.secondaryText}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
};

// Specialized component for customer search
export const CustomerSearchDropdown: React.FC<{
  value?: any;
  onChange: (customer: any) => void;
  onSearch: (searchTerm: string) => void;
  options: any[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  label?: string;
  required?: boolean;
}> = ({ value, onChange, onSearch, options, loading, error, placeholder, label, required }) => {
  return (
    <SearchableDropdown
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      options={options}
      loading={loading}
      error={error}
      placeholder={placeholder || 'Search by name, mobile, or email...'}
      displayKey="firstName"
      secondaryDisplayKey="mobileNo"
      searchKey="firstName"
      label={label}
      required={required}
      icon="bx bx-user"
      renderOption={(customer) => (
        <div className="py-1">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">{customer.firstName} {customer.lastName}</div>
              <small className="text-muted">{customer.mobileNo}</small>
            </div>
            <div className="text-end">
              <div className="fw-bold text-success">₹{customer.creditAmount?.toLocaleString()}</div>
              <small className="text-muted">Credit</small>
            </div>
          </div>
          <div className="mt-1">
            <small className="text-muted">{customer.email}</small>
          </div>
        </div>
      )}
      renderSelected={(customer) => `${customer.firstName} ${customer.lastName} - ${customer.mobileNo}`}
    />
  );
};

// Specialized component for sales person search
export const SalesPersonSearchDropdown: React.FC<{
  value?: any;
  onChange: (salesPerson: any) => void;
  onSearch: (searchTerm: string) => void;
  options: any[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  label?: string;
  required?: boolean;
}> = ({ value, onChange, onSearch, options, loading, error, placeholder, label, required }) => {
  return (
    <SearchableDropdown
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      options={options}
      loading={loading}
      error={error}
      placeholder={placeholder || 'Search sales person...'}
      displayKey="text"
      searchKey="text"
      label={label}
      required={required}
      icon="bx bx-user-check"
      minSearchLength={0}
      renderOption={(salesPerson) => (
        <div className="py-1 px-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">{salesPerson.text || salesPerson.name || '[No Name]'}</div>
              {salesPerson.employeeCode && (
                <small className="text-muted">Code: {salesPerson.employeeCode}</small>
              )}
            </div>
            <div className="text-end">
              {salesPerson.disabled && (
                <Badge bg="secondary">Disabled</Badge>
              )}
              {salesPerson.selected && (
                <Badge bg="success">Selected</Badge>
              )}
            </div>
          </div>
        </div>
      )}
      renderSelected={(salesPerson) => salesPerson.text || salesPerson.name || '[No Name]'}
    />
  );
};

// Enhanced Product Search Dropdown with API integration
export const ProductSearchDropdownWithAPI: React.FC<{
  value?: any;
  onChange: (product: any) => void;
  onProductDetails: (products: any[], searchType: 'bagNo' | 'styleNo' | 'igiNo' | 'huid') => void;
  searchType: 'bagNo' | 'styleNo' | 'igiNo' | 'huid';
  placeholder?: string;
  label?: string;
  required?: boolean;
}> = ({ value, onChange, onProductDetails, searchType, placeholder, label, required }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<any[]>([]);

  const handleSearch = useCallback(async (term: string) => {
    setSearchTerm(term);
    
    if (term.length < 2) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    // For the search dropdown, we'll show immediate results
    // The actual API call will happen on selection
    setIsOpen(true);
    setOptions([{ [getSearchKey()]: term, displayText: term }]);
  }, [searchType]);

  const getSearchKey = () => {
    switch (searchType) {
      case 'bagNo': return 'bagNo';
      case 'styleNo': return 'styleNo';
      case 'igiNo': return 'igiNo';
      case 'huid': return 'huidNo';
    }
  };

  const getSearchTypeNumber = () => {
    switch (searchType) {
      case 'bagNo': return 0;
      case 'styleNo': return 1;
      case 'igiNo': return 2;
      case 'huid': return 3;
    }
  };

  const handleSelect = async (item: any) => {
    const selectedValue = item[getSearchKey()] || item.displayText;
    setLoading(true);
    setError(null);
    setIsOpen(false);

    try {
      // Make API call to get product details
      const response = await apiClient.get('/api/v1/SalesEntry/GetProductDetailsForSalesEntry', {
        params: {
          SearchId: selectedValue,
          ProductSearchBy: getSearchTypeNumber()
        }
      });

      const productDetails = response.data;
      
      if (productDetails && productDetails.length > 0) {
        if (productDetails.length === 1) {
          // Single product - directly select it
          onChange(productDetails[0]);
        } else {
          // Multiple products - show modal
          onProductDetails(productDetails, searchType);
        }
      } else {
        setError('No product found for the selected value');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case 'bagNo': return placeholder || 'Search by bag number...';
      case 'styleNo': return placeholder || 'Search by style number...';
      case 'igiNo': return placeholder || 'Search by IGI number...';
      case 'huid': return placeholder || 'Search by HUID...';
    }
  };

  return (
    <Form.Group className="mb-3">
      {label && <Form.Label>{label}{required && <span className="text-danger">*</span>}</Form.Label>}
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={getPlaceholder()}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => options.length > 0 && setIsOpen(true)}
          className={error ? 'is-invalid' : ''}
        />
        {loading && (
          <div className="position-absolute end-0 top-50 translate-middle-y me-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        {error && <div className="invalid-feedback">{error}</div>}
        {isOpen && options.length > 0 && (
          <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000 }}>
            {options.map((option, index) => (
              <div
                key={index}
                className="p-2 cursor-pointer hover-bg-light"
                onClick={() => handleSelect(option)}
                style={{ cursor: 'pointer' }}
              >
                <div className="fw-bold">{option.displayText}</div>
                <div className="small text-muted">Press Enter to search</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
};

// Specialized component for bag number search
export const BagNoSearchDropdown: React.FC<{
  value?: any;
  onChange: (product: any) => void;
  onSearch: (searchTerm: string) => void;
  options: any[];
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  label?: string;
  required?: boolean;
}> = ({ value, onChange, onSearch, options, loading, error, placeholder, label, required }) => {
  return (
    <SearchableDropdown
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      options={options}
      loading={loading}
      error={error}
      placeholder={placeholder || 'Search by bag number...'}
      displayKey="bagNo"
      secondaryDisplayKey="styleNo"
      searchKey="bagNo"
      label={label}
      required={required}
      icon="bx bx-package"
      minSearchLength={1}
      renderOption={(product) => (
        <div className="py-1">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">{product.bagNo}</div>
              <small className="text-muted">{product.styleNo}</small>
            </div>
            <div className="text-end">
              <div className="fw-bold">₹{product.mrp?.toLocaleString()}</div>
              <small className="text-muted">{product.category}</small>
            </div>
          </div>
          <div className="mt-1">
            <small className="text-muted">
              {product.kt}K | {product.netWt}g | {product.pcs} pcs
            </small>
          </div>
          {product.stockQuantity !== undefined && (
            <div className="mt-1">
              <Badge bg={product.stockQuantity > 0 ? 'success' : 'danger'} className="me-1">
                Stock: {product.stockQuantity}
              </Badge>
              {product.isSNMCC && <Badge bg="info">SNMCC</Badge>}
            </div>
          )}
        </div>
      )}
      renderSelected={(product) => `${product.bagNo} - ${product.styleNo}`}
    />
  );
};